import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  FaEye, FaFilePdf, FaDownload, FaEnvelope, FaBell, FaMoneyBillWave, FaBan,
  FaPlus, FaPlay, FaInfoCircle, FaTimes, FaUndo, FaCheckCircle
} from 'react-icons/fa';
import axios from '../axiosInstance';
import { UserContext } from '../UserContext';
import { useDialog } from '../components/ConfirmProvider';
import Pagination from '../components/Pagination';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import '../Pips/Pips.css';
import '../Pages.css';
import '../Customers/Customers.css';

// Organisation invoices. PIP Intel staff (users without an organisation)
// see every organisation's invoices: they view / download them, issue and
// send them by hand, capture payments and void. An organisation's users with
// invoices.view_own see their own organisation's invoices, read-only. The
// API enforces the same rules. Invoices are issued by the daily billing
// sweep 5 days before month end.

const STATUS_LABEL = {
  unpaid: 'Unpaid', partially_paid: 'Part paid', overdue: 'Overdue', paid: 'Paid', void: 'Void', superseded: 'Superseded'
};
// Reuse the KYC status pill colours.
const STATUS_PILL = {
  unpaid: 'pending', partially_paid: 'suspended', overdue: 'rejected', paid: 'active', void: 'closed', superseded: 'closed'
};
// No longer open: void, or replaced by a newer invoice that carries its months.
const isClosed = (inv) => inv.status === 'void' || inv.status === 'superseded';
const STATUS_FILTERS = [
  ['all', 'All statuses'], ['outstanding', 'Outstanding'], ['overdue', 'Overdue'],
  ['unpaid', 'Unpaid'], ['partially_paid', 'Part paid'], ['paid', 'Paid'], ['superseded', 'Superseded'], ['void', 'Void']
];
const METHODS = [['eft', 'EFT'], ['cash', 'Cash'], ['card', 'Card'], ['cheque', 'Cheque'], ['other', 'Other']];
const EMAIL_KIND = {
  invoice: 'Invoice', reminder: 'Reminder', suspension: 'Suspension notice',
  payment_received: 'Payment received', payment_undone: 'Payment reversed'
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const money = (n, ccy = 'NAD') => {
  const v = Math.round(Number(n || 0) * 100) / 100;
  const [whole, cents] = Math.abs(v).toFixed(2).split('.');
  return `${v < 0 ? '-' : ''}${ccy === 'NAD' ? 'N$' : ccy} ${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${cents}`;
};
const monthLabel = (iso) => {
  if (!iso) return '—';
  const [y, m] = String(iso).split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
};
// 'October 2026', or 'March 2026 – October 2026' for an invoice spanning months.
const periodLabel = (start, end) => {
  const a = monthLabel(start);
  const b = end ? monthLabel(end) : a;
  return a === b ? a : `${a} – ${b}`;
};
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const todayIso = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const hasPerm = (user, name) =>
  !!user && (user.is_system_admin === true || (Array.isArray(user.permissions) && user.permissions.includes(name)));

// Error text from a JSON or (PDF request) Blob error response.
async function errorText(err, fallback) {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const body = JSON.parse(await data.text());
      return body?.error?.message || body?.error || fallback;
    } catch (_) { return fallback; }
  }
  return (typeof data?.error === 'string' && data.error) || err.message || fallback;
}

// ' The managers were emailed …' / ' The email to the managers failed: …' after a payment change.
const emailOutcome = (email) => {
  if (!email) return '';
  return email.sent
    ? ` The organisation managers were emailed the updated invoice (${email.to.join(', ')}).`
    : ` The organisation managers were not emailed: ${email.error}`;
};

function StatusPill({ status }) {
  return <span className={`un-meta__pill cust-pill--status-${STATUS_PILL[status]}`}>{STATUS_LABEL[status]}</span>;
}

function ManageInvoices() {
  const { user } = useContext(UserContext);
  const { confirm } = useDialog();
  const isStaff = !!user && (user.is_system_admin === true || !user.organisation_id);
  // An organisation's own invoices, read-only.
  const ownView = !isStaff && hasPerm(user, 'invoices.view_own');
  const can = {
    create: isStaff && hasPerm(user, 'invoices.create'),
    send: isStaff && hasPerm(user, 'invoices.send'),
    pay: isStaff && hasPerm(user, 'invoices.capture_payment'),
    void: isStaff && hasPerm(user, 'invoices.void')
  };

  const [rows, setRows] = useState([]);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [detail, setDetail] = useState(null);       // { invoice, organisation, payments, emails, recipients }
  const [pdfView, setPdfView] = useState(null);     // { url, number }
  const [payment, setPayment] = useState(null);     // { invoice, form }
  const [issue, setIssue] = useState(null);         // form
  const [voiding, setVoiding] = useState(null);     // { invoice, reason }
  const [undoing, setUndoing] = useState(null);     // { invoice, payment, reason }
  const [lastPayment, setLastPayment] = useState(null); // { invoice, payment } — Undo in the notice

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, billing] = await Promise.all([
        axios.get('/invoices'),
        isStaff ? axios.get('/invoices/billing-info') : Promise.resolve({ data: null })
      ]);
      setRows(Array.isArray(list.data) ? list.data : []);
      setInfo(billing.data || null);
      setError(null);
    } catch (err) {
      setError(await errorText(err, 'Could not load invoices.'));
    } finally {
      setLoading(false);
    }
  }, [isStaff]);

  useEffect(() => { if (isStaff || ownView) fetchAll(); }, [isStaff, ownView, fetchAll]);
  useEffect(() => () => { if (pdfView) URL.revokeObjectURL(pdfView.url); }, [pdfView]);

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`/invoices/${id}`);
      // Older API versions don't return every list: default them so the window still opens.
      setDetail({
        lines: [], outstanding: [], payments: [], emails: [],
        ...res.data,
        recipients: { to: [], cc: [], ...(res.data?.recipients || {}) }
      });
    } catch (err) {
      setError(await errorText(err, 'Could not open the invoice.'));
    }
  };

  // After any change: refresh the list, and the open detail if it's that invoice.
  const refresh = async (id) => {
    await fetchAll();
    if (detail && detail.invoice.id === id) await openDetail(id);
  };

  const fetchPdf = async (inv) => {
    const res = await axios.get(`/invoices/${inv.id}/pdf`, { responseType: 'blob' });
    return new Blob([res.data], { type: 'application/pdf' });
  };

  const viewPdf = async (inv) => {
    try {
      const blob = await fetchPdf(inv);
      setPdfView({ url: URL.createObjectURL(blob), number: inv.invoice_number, invoice: inv });
    } catch (err) {
      setError(await errorText(err, 'Could not open the PDF.'));
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const blob = await fetchPdf(inv);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(await errorText(err, 'Could not download the PDF.'));
    }
  };

  const send = async (inv, kind) => {
    const what = kind === 'reminder' ? 'a payment reminder for' : 'invoice';
    const number = inv.status === 'superseded' && inv.superseded_by_number ? inv.superseded_by_number : inv.invoice_number;
    const ok = await confirm({
      title: kind === 'reminder' ? 'Send payment reminder' : 'Email invoice',
      message: `Email ${what} ${number} to ${inv.organisation_name}'s organisation managers, with the PDF attached?`,
      confirmLabel: 'Send email'
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await axios.post(`/invoices/${inv.id}/send`, { kind });
      const sent = res.data.invoice_number || inv.invoice_number;
      setLastPayment(null);
      setNotice(`${kind === 'reminder' ? 'Reminder' : 'Invoice'} ${sent} emailed to ${res.data.to.join(', ')}` +
        `${res.data.cc.length ? ` (cc ${res.data.cc.join(', ')})` : ''}.` +
        (sent !== inv.invoice_number ? ` ${inv.invoice_number} was replaced by ${sent}, which carries all the unpaid months.` : ''));
      await fetchAll();
      setError(null);
      await refresh(inv.id);
    } catch (err) {
      setError(await errorText(err, 'The email was not sent.'));
      await refresh(inv.id);
    } finally {
      setBusy(false);
    }
  };

  const runBilling = async () => {
    setBusy(true);
    try {
      const r = (await axios.post('/invoices/run-billing')).data;
      const repriced = (r.repriced_invoices || [])
        .map((c) => `${c.invoice_number} ${money(c.old_total)} → ${money(c.new_total)}${c.settled ? ' (now paid — nothing to pay)' : ''}`);
      setNotice(`Billing run: ${plural(r.repriced || 0, 'invoice')} re-priced${repriced.length ? ` (${repriced.join('; ')})` : ''}, ` +
        `${r.issued} invoice${r.issued === 1 ? '' : 's'} issued, ` +
        `${r.suspended || 0} organisation${r.suspended === 1 ? '' : 's'} suspended. Nothing was emailed.` +
        `${r.errors?.length ? ` ${r.errors.length} error(s): ${r.errors.map((e) => e.organisation_name ? `${e.organisation_name} — ${e.error}` : e.error).join('; ')}` : ''}`);
      setError(null);
      await fetchAll();
    } catch (err) {
      setError(await errorText(err, 'The billing run failed.'));
    } finally {
      setBusy(false);
    }
  };

  const openIssue = () => {
    const period = info?.current_period || todayIso().slice(0, 7);
    setIssue({ organisation_id: '', period, amount: '', description: '', due_date: '', notes: '', send: true });
  };

  const submitIssue = async (e) => {
    e.preventDefault();
    if (!issue.organisation_id) { setError('Choose the organisation to invoice.'); return; }
    setBusy(true);
    try {
      const body = {
        organisation_id: Number(issue.organisation_id),
        period: issue.period,
        amount: issue.amount === '' ? null : Number(issue.amount),
        description: issue.description.trim() || null,
        due_date: issue.due_date || null,
        notes: issue.notes.trim() || null,
        send: issue.send
      };
      const res = await axios.post('/invoices', body);
      const { invoice, email } = res.data;
      setIssue(null);
      setError(email && !email.sent ? `Invoice ${invoice.invoice_number} was issued but not emailed: ${email.error}` : null);
      setNotice(`Invoice ${invoice.invoice_number} issued to ${invoice.organisation_name} for ${money(invoice.total, invoice.currency)}` +
        `${email?.sent ? ` and emailed to ${email.to.join(', ')}` : ''}.`);
      await fetchAll();
    } catch (err) {
      setError(await errorText(err, 'The invoice was not issued.'));
    } finally {
      setBusy(false);
    }
  };

  const openPayment = (inv) => setPayment({
    invoice: inv,
    form: { amount: String(inv.balance), paid_on: todayIso(), method: 'eft', reference: '', notes: '' }
  });

  const submitPayment = async (e) => {
    e.preventDefault();
    const inv = payment.invoice;
    setBusy(true);
    try {
      const f = payment.form;
      const res = await axios.post(`/invoices/${inv.id}/payments`, {
        amount: Number(f.amount),
        paid_on: f.paid_on,
        method: f.method,
        reference: f.reference.trim() || null,
        notes: f.notes.trim() || null
      });
      const updated = res.data.invoice;
      setPayment(null);
      setError(null);
      setNotice(`Payment of ${money(f.amount, inv.currency)} captured on ${inv.invoice_number}. ` +
        (updated.balance > 0 ? `Balance due: ${money(updated.balance, inv.currency)}.` : 'The invoice is paid in full.') +
        (res.data.reactivated ? ` ${inv.organisation_name} has been reactivated.` : '') +
        emailOutcome(res.data.email));
      setLastPayment(res.data.payment_id
        ? { invoice: updated, payment: { id: res.data.payment_id, amount: Number(f.amount), paid_on: f.paid_on } }
        : null);
      await refresh(inv.id);
    } catch (err) {
      setError(await errorText(err, 'The payment was not captured.'));
    } finally {
      setBusy(false);
    }
  };

  // Undo from the table: the latest payment still counting on the invoice.
  const undoFromRow = async (inv) => {
    try {
      const res = await axios.get(`/invoices/${inv.id}`);
      const live = (res.data?.payments || []).filter((p) => !p.undone_at);
      const p = live[live.length - 1];
      if (!p) { setError(`${inv.invoice_number} has no payment to undo.`); return; }
      setUndoing({ invoice: res.data.invoice || inv, payment: p, reason: '' });
    } catch (err) {
      setError(await errorText(err, 'Could not load the payments.'));
    }
  };

  const submitUndo = async (e) => {
    e.preventDefault();
    const { invoice: inv, payment: p, reason } = undoing;
    setBusy(true);
    try {
      const res = await axios.post(`/invoices/${inv.id}/payments/${p.id}/undo`, { reason });
      setUndoing(null);
      setLastPayment(null);
      setError(null);
      setNotice(`Payment of ${money(p.amount, inv.currency)} (paid ${formatDate(p.paid_on)}) undone on ${inv.invoice_number}. ` +
        `Balance due: ${money(res.data.invoice.balance, inv.currency)}.` + emailOutcome(res.data.email));
      await refresh(inv.id);
    } catch (err) {
      setError(await errorText(err, 'The payment was not undone.'));
    } finally {
      setBusy(false);
    }
  };

  const submitVoid = async (e) => {
    e.preventDefault();
    const inv = voiding.invoice;
    setBusy(true);
    try {
      const res = await axios.post(`/invoices/${inv.id}/void`, { reason: voiding.reason });
      setVoiding(null);
      setError(null);
      setNotice(`Invoice ${inv.invoice_number} voided.${res.data.reactivated ? ` ${inv.organisation_name} has been reactivated.` : ''}`);
      await refresh(inv.id);
    } catch (err) {
      setError(await errorText(err, 'The invoice was not voided.'));
    } finally {
      setBusy(false);
    }
  };

  const organisations = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.organisation_id, r.organisation_name));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return rows.filter((r) =>
      (statusFilter === 'all' ||
        (statusFilter === 'outstanding' ? (r.status === 'unpaid' || r.status === 'partially_paid') : r.display_status === statusFilter)) &&
      (orgFilter === 'all' || String(r.organisation_id) === orgFilter) &&
      (!periodFilter || r.period_start.slice(0, 7) === periodFilter) &&
      (!q || [r.invoice_number, r.organisation_name, r.description].some((v) => String(v || '').toLowerCase().includes(q)))
    );
  }, [rows, searchTerm, statusFilter, orgFilter, periodFilter]);

  const live = rows.filter((r) => !isClosed(r));
  const outstanding = live.reduce((s, r) => s + r.balance, 0);
  const overdue = rows.filter((r) => r.display_status === 'overdue');
  const suspended = (info?.organisations || []).filter((o) => o.billing_suspended_at);

  const page = Math.min(currentPage, Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
  const pageItems = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const resetPage = () => setCurrentPage(1);

  if (!isStaff && !ownView) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> You do not have access to invoices.</span>
        </div>
      </div>
    );
  }

  const issueOrg = issue && (info?.organisations || []).find((o) => String(o.id) === String(issue.organisation_id));
  const muted = { fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)' };

  const rowActions = (r) => (
    <>
      <button title="Details" className="action-button" onClick={() => openDetail(r.id)}><FaEye /></button>
      <button title="View PDF" className="action-button" onClick={() => viewPdf(r)}><FaFilePdf /></button>
      <button title="Download PDF" className="action-button" onClick={() => downloadPdf(r)}><FaDownload /></button>
      {can.send && !isClosed(r) && (
        <button title="Email invoice" className="action-button" disabled={busy} onClick={() => send(r, 'invoice')}><FaEnvelope /></button>
      )}
      {can.send && r.display_status === 'overdue' && (
        <button title="Send payment reminder" className="action-button" disabled={busy} onClick={() => send(r, 'reminder')}><FaBell /></button>
      )}
      {can.pay && !isClosed(r) && r.balance > 0 && (
        <button title="Capture payment" className="action-button" disabled={busy} onClick={() => openPayment(r)}><FaMoneyBillWave /></button>
      )}
      {can.pay && !isClosed(r) && r.amount_paid > 0 && (
        <button title="Undo a payment" className="action-button" disabled={busy} onClick={() => undoFromRow(r)}><FaUndo /></button>
      )}
      {can.void && !isClosed(r) && r.status !== 'paid' && r.amount_paid === 0 && (
        <button title="Void invoice" className="action-button" disabled={busy} onClick={() => setVoiding({ invoice: r, reason: '' })}><FaBan /></button>
      )}
    </>
  );

  return (
    <div className="page-container">
      <div className="stats-section">
        <div className="stats-layout__rest">
          <div className="stats-grid">
            <div className="stat-card stat-card--blue">
              <div className="stat-label">Invoices</div>
              <div className="stat-value stat-value--blue">{live.length}</div>
            </div>
            <div className="stat-card stat-card--orange">
              <div className="stat-label">Outstanding</div>
              <div className="stat-value stat-value--orange">{money(outstanding)}</div>
            </div>
            <div className="stat-card stat-card--purple">
              <div className="stat-label">Overdue</div>
              <div className="stat-value stat-value--purple">{overdue.length}</div>
              <div style={{ ...muted, marginTop: '0.35rem' }}>{money(overdue.reduce((s, r) => s + r.balance, 0))}</div>
            </div>
            {isStaff ? (
              <div className="stat-card stat-card--brown">
                <div className="stat-label">Suspended Organisations</div>
                <div className="stat-value stat-value--brown">{suspended.length}</div>
                {suspended.length > 0 && (
                  <div style={{ ...muted, marginTop: '0.35rem' }}>{suspended.map((o) => o.name).join(', ')}</div>
                )}
              </div>
            ) : (
              <div className="stat-card stat-card--brown">
                <div className="stat-label">Paid</div>
                <div className="stat-value stat-value--brown">{money(live.reduce((s, r) => s + r.amount_paid, 0))}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ margin: '1rem 0 2rem' }}>
        <div className="table-controls">
          <div style={{ position: 'relative', flex: '1 1 auto', maxWidth: '300px' }}>
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); resetPage(); }}
              className="search-filter"
              style={{ width: '100%', maxWidth: 'none', paddingRight: '2.25rem', boxSizing: 'border-box' }}
            />
            {searchTerm && (
              <span
                role="button"
                tabIndex={0}
                title="Clear search"
                aria-label="Clear search"
                onClick={() => { setSearchTerm(''); resetPage(); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setSearchTerm(''); resetPage(); } }}
                style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--text-secondary, #607d8b)', display: 'flex' }}
              >
                <FaTimes />
              </span>
            )}
          </div>
          {can.create && (
            <div className="button-group">
              <button className="btn-cancel" onClick={runBilling} disabled={busy}><FaPlay style={{ marginRight: 5 }} /> Run billing now</button>
              <button className="export-button" onClick={openIssue} disabled={busy || !info}><FaPlus style={{ marginRight: 5 }} /> Issue invoice</button>
            </div>
          )}
        </div>
      </div>

      {notice && (
        <div className="alert-banner alert-banner--success">
          <span className="alert-banner__body" style={{ flex: 1 }}><FaCheckCircle /> {notice}</span>
          {lastPayment && can.pay && (
            <button type="button" className="btn-cancel" style={{ marginRight: 10 }} disabled={busy}
              onClick={() => setUndoing({ invoice: lastPayment.invoice, payment: lastPayment.payment, reason: '' })}>
              <FaUndo style={{ marginRight: 5 }} /> Undo payment
            </button>
          )}
          <span role="button" tabIndex={0} title="Dismiss" style={{ cursor: 'pointer' }} onClick={() => { setNotice(null); setLastPayment(null); }}><FaTimes /></span>
        </div>
      )}
      {error && (
        <div className="alert-banner alert-banner--danger">
          <span className="alert-banner__body" style={{ flex: 1 }}><FaInfoCircle /> {error}</span>
          <span role="button" tabIndex={0} title="Dismiss" style={{ cursor: 'pointer' }} onClick={() => setError(null)}><FaTimes /></span>
        </div>
      )}

      {loading && <p>Loading invoices…</p>}

      {!loading && rows.length === 0 && !error && (
        <p style={{ color: 'var(--text-secondary)' }}>
          {isStaff
            ? <>No invoices yet. They are issued automatically on {info ? formatDate(info.issue_day) : 'the billing day'}, or use Issue invoice.</>
            : 'Your organisation has no invoices yet.'}
        </p>
      )}

      {!loading && rows.length > 0 && (
        <>
          <div className="search-results-info" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div className="search-count" style={{ flex: 1, fontSize: '0.8rem' }}>
              Showing {filtered.length} of {rows.length} invoice{rows.length === 1 ? '' : 's'}
            </div>
            <select className="table-search-input" style={{ width: 'auto' }} value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); resetPage(); }} title="Filter by status">
              {STATUS_FILTERS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
            {isStaff && (
              <select className="table-search-input" style={{ width: 'auto' }} value={orgFilter}
                onChange={(e) => { setOrgFilter(e.target.value); resetPage(); }} title="Filter by organisation">
                <option value="all">All organisations</option>
                {organisations.map(([id, name]) => <option key={id} value={String(id)}>{name}</option>)}
              </select>
            )}
            <input type="month" className="table-search-input" style={{ width: 'auto' }} value={periodFilter}
              onChange={(e) => { setPeriodFilter(e.target.value); resetPage(); }} title="Filter by billing month" />
            {periodFilter && (
              <button type="button" className="btn-cancel" onClick={() => { setPeriodFilter(''); resetPage(); }}>All months</button>
            )}
          </div>

          <div className="table-container">
            <table className="pips-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  {isStaff && <th>Organisation</th>}
                  {isStaff && <th>Onboarded</th>}
                  <th>Period</th>
                  <th>Invoice date</th>
                  <th>Due date</th>
                  <th>Total</th>
                  <th>Balance</th>
                  <th>Status</th>
                  {isStaff && <th>Emailed</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((r) => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{r.invoice_number}</td>
                    {isStaff && (
                      <td>
                        {r.organisation_name}
                        {r.billing_suspended_at && <div><span className="un-meta__pill cust-pill--status-rejected">Suspended</span></div>}
                      </td>
                    )}
                    {isStaff && <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.organisation_onboarded_on)}</td>}
                    <td>
                      {periodLabel(r.period_start, r.period_end)}
                      {r.months > 1 && <div style={muted}>{plural(r.months, 'month')}</div>}
                    </td>
                    <td>{formatDate(r.issue_date)}</td>
                    <td>
                      {formatDate(r.due_date)}
                      {r.days_overdue > 0 && <div style={muted}>{r.days_overdue} day{r.days_overdue === 1 ? '' : 's'} overdue</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{money(r.total, r.currency)}</td>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: r.balance > 0 ? 600 : 400 }}>{money(r.balance, r.currency)}</td>
                    <td>
                      <StatusPill status={r.display_status} />
                      {r.superseded_by_number && <div style={muted}>by {r.superseded_by_number}</div>}
                    </td>
                    {isStaff && (
                      <td style={muted}>
                        {r.sent_at ? formatDateTime(r.sent_at) : 'Not yet'}
                        {r.reminder_count > 0 && <div>{r.reminder_count} reminder{r.reminder_count === 1 ? '' : 's'}</div>}
                      </td>
                    )}
                    {/* Every action button stays visible (the narrow-screen table rule clips cells). */}
                    <td style={{ whiteSpace: 'nowrap', maxWidth: 'none', overflow: 'visible', textOverflow: 'clip' }}>{rowActions(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={page}
            totalItems={filtered.length}
            itemsPerPage={itemsPerPage}
            itemLabel="invoice"
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); resetPage(); }}
          />
        </>
      )}

      {/* ── Invoice details ── */}
      {detail && (
        <div className="modal-overlay" onClick={() => !busy && setDetail(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
            <button className="modal-close" onClick={() => !busy && setDetail(null)}>×</button>
            <h2 style={{ marginTop: 0 }}>
              Invoice {detail.invoice.invoice_number} <StatusPill status={detail.invoice.display_status} />
            </h2>

            {detail.invoice.status === 'superseded' && (
              <div className="alert-banner alert-banner--info">
                <span className="alert-banner__body">
                  <FaInfoCircle /> Replaced by {detail.invoice.superseded_by_number || 'a newer invoice'}, which carries its unpaid months and payments.
                  {isStaff && ` Emailing this invoice sends ${detail.invoice.superseded_by_number || 'the latest one'} instead.`}
                </span>
              </div>
            )}
            {(detail.replaces || []).length > 0 && (
              <div className="alert-banner alert-banner--info">
                <span className="alert-banner__body">
                  <FaInfoCircle /> Includes the unpaid months of {detail.replaces.map((r) => r.invoice_number).join(', ')}, which it replaces.
                </span>
              </div>
            )}

            <div className="form-grid-2" style={{ fontSize: '0.9rem' }}>
              <div>
                <div style={muted}>Organisation</div>
                <strong>{detail.organisation.name}</strong>
                {detail.organisation.billing_suspended_at && (
                  <div style={{ color: '#b71c1c', fontSize: '0.8rem' }}>
                    Suspended for non-payment since {formatDateTime(detail.organisation.billing_suspended_at)}
                  </div>
                )}
                {!detail.organisation.is_active && !detail.organisation.billing_suspended_at && (
                  <div style={muted}>Organisation is inactive</div>
                )}
              </div>
              <div>
                <div style={muted}>Billing period</div>
                {periodLabel(detail.invoice.period_start, detail.invoice.period_end)} ({plural(detail.lines.length || 1, 'month')})
              </div>
              <div>
                <div style={muted}>Invoice date · Due date</div>
                {formatDate(detail.invoice.issue_date)} · {formatDate(detail.invoice.due_date)}
                {detail.invoice.days_overdue > 0 && <span style={{ color: '#b71c1c' }}> ({detail.invoice.days_overdue} days overdue)</span>}
              </div>
              <div>
                <div style={muted}>Description</div>
                {detail.invoice.description}
              </div>
              <div>
                <div style={muted}>Amount</div>
                {money(detail.invoice.subtotal, detail.invoice.currency)}
                {detail.invoice.vat_amount > 0 && ` + VAT ${money(detail.invoice.vat_amount, detail.invoice.currency)}`}
                {' = '}<strong>{money(detail.invoice.total, detail.invoice.currency)}</strong>
              </div>
              <div>
                <div style={muted}>Paid · Balance due</div>
                {money(detail.invoice.amount_paid, detail.invoice.currency)} · <strong>{money(detail.invoice.balance, detail.invoice.currency)}</strong>
              </div>
              {isStaff && (
                <div className="form-group--full">
                  <div style={muted}>Emails go to</div>
                  {detail.recipients.to.length ? detail.recipients.to.join(', ') : <span style={{ color: '#b71c1c' }}>No active organisation manager — emails cannot be sent</span>}
                  {detail.recipients.cc.length > 0 && <span style={muted}> · cc {detail.recipients.cc.join(', ')}</span>}
                </div>
              )}
              {detail.invoice.notes && (
                <div className="form-group--full"><div style={muted}>Notes</div>{detail.invoice.notes}</div>
              )}
              {detail.invoice.status === 'void' && (
                <div className="form-group--full">
                  <div style={muted}>Voided</div>
                  {formatDateTime(detail.invoice.voided_at)}: {detail.invoice.void_reason}
                </div>
              )}
            </div>

            <h3 style={{ marginBottom: '0.5rem' }}>Months billed</h3>
            <div className="table-container">
              <table className="pips-table">
                <thead><tr><th>Month</th><th>Description</th><th>Amount</th></tr></thead>
                <tbody>
                  {detail.lines.map((l) => (
                    <tr key={l.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{monthLabel(l.period_start)}</td>
                      <td>{l.description}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{money(l.amount, detail.invoice.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {detail.outstanding.length > 0 && detail.invoice.status !== 'void' && (
              <div className="alert-banner alert-banner--warning" style={{ marginTop: '1rem' }}>
                <span className="alert-banner__body">
                  <FaInfoCircle /> Also unpaid: {detail.outstanding.map((o) => `${o.invoice_number} (${periodLabel(o.period_start, o.period_end)}, ${money(o.balance, detail.invoice.currency)})`).join(', ')}.
                  {' '}Total outstanding on the account: <strong>{money(detail.outstanding.reduce((s, o) => s + o.balance, 0) + detail.invoice.balance, detail.invoice.currency)}</strong>.
                  {' '}These are listed on the PDF{isStaff ? ' and in the email' : ''}.
                </span>
              </div>
            )}

            <h3 style={{ marginBottom: '0.5rem' }}>Payments</h3>
            {detail.payments.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>No payments captured.</p>
            ) : (
              <div className="table-container">
                <table className="pips-table">
                  <thead>
                    <tr><th>Date</th><th>Amount</th><th>Method</th><th>Reference</th><th>{isStaff ? 'Captured by' : 'Recorded'}</th>{can.pay && !isClosed(detail.invoice) && <th />}</tr>
                  </thead>
                  <tbody>
                    {detail.payments.map((p) => (
                      <tr key={p.id} style={p.undone_at ? { color: 'var(--text-secondary,#607d8b)' } : undefined}>
                        <td>{formatDate(p.paid_on)}</td>
                        <td style={p.undone_at ? { textDecoration: 'line-through' } : undefined}>{money(p.amount, detail.invoice.currency)}</td>
                        <td>{(METHODS.find(([v]) => v === p.method) || [p.method, p.method])[1]}</td>
                        <td>
                          {p.reference || '—'}{p.notes && <div style={muted}>{p.notes}</div>}
                          {p.original_invoice_number && <div style={muted}>captured on {p.original_invoice_number}</div>}
                        </td>
                        <td>
                          {isStaff && (p.captured_by_name || '—')}<div style={muted}>{formatDateTime(p.created_at)}</div>
                          {p.undone_at && (
                            <div style={{ ...muted, color: '#b71c1c' }}>
                              {isStaff
                                ? `Undone ${formatDateTime(p.undone_at)}${p.undone_by_name ? ` by ${p.undone_by_name}` : ''}: ${p.undo_reason}`
                                : `Reversed ${formatDateTime(p.undone_at)}`}
                            </div>
                          )}
                        </td>
                        {can.pay && !isClosed(detail.invoice) && (
                          <td>
                            {!p.undone_at && (
                              <button title="Undo payment" className="btn-cancel" disabled={busy} style={{ whiteSpace: 'nowrap' }}
                                onClick={() => setUndoing({ invoice: detail.invoice, payment: p, reason: '' })}><FaUndo style={{ marginRight: 5 }} /> Undo</button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {isStaff && <h3 style={{ marginBottom: '0.5rem' }}>Emails</h3>}
            {!isStaff ? null : detail.emails.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>Not emailed yet.</p>
            ) : (
              <div className="table-container">
                <table className="pips-table">
                  <thead><tr><th>When</th><th>Type</th><th>To</th><th>Result</th></tr></thead>
                  <tbody>
                    {detail.emails.map((m) => (
                      <tr key={m.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(m.created_at)}<div style={muted}>{m.sent_by_name || 'Billing run'}</div></td>
                        <td>{EMAIL_KIND[m.kind] || m.kind}</td>
                        <td>{m.recipients.join(', ') || '—'}{m.cc.length > 0 && <div style={muted}>cc {m.cc.join(', ')}</div>}</td>
                        <td>{m.success ? 'Sent' : <span style={{ color: '#b71c1c' }}>Failed: {m.error}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn-cancel" onClick={() => viewPdf(detail.invoice)}><FaFilePdf style={{ marginRight: 5 }} /> View PDF</button>
              <button className="btn-cancel" onClick={() => downloadPdf(detail.invoice)}><FaDownload style={{ marginRight: 5 }} /> Download PDF</button>
              {can.send && detail.invoice.status !== 'void' && (
                <button className="btn-cancel" disabled={busy} onClick={() => send(detail.invoice, 'invoice')}>
                  <FaEnvelope style={{ marginRight: 5 }} /> {detail.invoice.status === 'superseded' ? `Email ${detail.invoice.superseded_by_number || 'the latest invoice'}` : 'Email invoice'}
                </button>
              )}
              {can.send && detail.invoice.display_status === 'overdue' && (
                <button className="btn-cancel" disabled={busy} onClick={() => send(detail.invoice, 'reminder')}><FaBell style={{ marginRight: 5 }} /> Send reminder</button>
              )}
              {can.pay && !isClosed(detail.invoice) && detail.invoice.balance > 0 && (
                <button className="export-button" disabled={busy} onClick={() => openPayment(detail.invoice)}><FaMoneyBillWave style={{ marginRight: 5 }} /> Capture payment</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── PDF viewer ── */}
      {pdfView && (
        <div className="modal-overlay" onClick={() => setPdfView(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900, width: '95vw' }}>
            <button className="modal-close" onClick={() => setPdfView(null)}>×</button>
            <h2 style={{ marginTop: 0 }}>Invoice {pdfView.number}</h2>
            <iframe title={`Invoice ${pdfView.number}`} src={pdfView.url} style={{ width: '100%', height: '75vh', border: '1px solid var(--border-color, #dfe3e8)' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
              <button className="export-button" onClick={() => downloadPdf(pdfView.invoice)}><FaDownload style={{ marginRight: 5 }} /> Download PDF</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Capture payment ── */}
      {payment && (
        <div className="modal-overlay" onClick={() => !busy && setPayment(null)}>
          <form className="modal-content" onClick={(e) => e.stopPropagation()} onSubmit={submitPayment} style={{ maxWidth: 560 }}>
            <button type="button" className="modal-close" onClick={() => !busy && setPayment(null)}>×</button>
            <h2 style={{ marginTop: 0 }}><FaMoneyBillWave style={{ marginRight: '0.4rem' }} />Capture payment</h2>
            <p style={{ marginTop: 0 }}>
              {payment.invoice.invoice_number} · {payment.invoice.organisation_name} · balance due{' '}
              <strong>{money(payment.invoice.balance, payment.invoice.currency)}</strong>
            </p>
            <div className="form-grid-2">
              <div className="form-group">
                <label>Amount ({payment.invoice.currency})</label>
                <input type="number" step="0.01" min="0.01" max={payment.invoice.balance} required value={payment.form.amount}
                  onChange={(e) => setPayment((p) => ({ ...p, form: { ...p.form, amount: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label>Date paid</label>
                <input type="date" required max={todayIso()} value={payment.form.paid_on}
                  onChange={(e) => setPayment((p) => ({ ...p, form: { ...p.form, paid_on: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label>Method</label>
                <select value={payment.form.method}
                  onChange={(e) => setPayment((p) => ({ ...p, form: { ...p.form, method: e.target.value } }))}>
                  {METHODS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Reference</label>
                <input type="text" maxLength={100} placeholder="e.g. bank reference" value={payment.form.reference}
                  onChange={(e) => setPayment((p) => ({ ...p, form: { ...p.form, reference: e.target.value } }))} />
              </div>
              <div className="form-group form-group--full">
                <label>Notes</label>
                <textarea rows={2} maxLength={1000} value={payment.form.notes}
                  onChange={(e) => setPayment((p) => ({ ...p, form: { ...p.form, notes: e.target.value } }))} />
              </div>
            </div>
            {payment.invoice.billing_suspended_at && (
              <p style={muted}>{payment.invoice.organisation_name} is suspended for non-payment; settling its overdue invoices reactivates it.</p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-cancel" disabled={busy} onClick={() => setPayment(null)}>Cancel</button>
              <button type="submit" className="export-button" disabled={busy}>{busy ? 'Saving…' : 'Capture payment'}</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Issue invoice ── */}
      {issue && (
        <div className="modal-overlay" onClick={() => !busy && setIssue(null)}>
          <form className="modal-content" onClick={(e) => e.stopPropagation()} onSubmit={submitIssue} style={{ maxWidth: 640 }}>
            <button type="button" className="modal-close" onClick={() => !busy && setIssue(null)}>×</button>
            <h2 style={{ marginTop: 0 }}><FaPlus style={{ marginRight: '0.4rem' }} />Issue invoice</h2>
            <div className="form-grid-2">
              <div className="form-group form-group--full">
                <label>Organisation</label>
                <select required value={issue.organisation_id}
                  onChange={(e) => setIssue((f) => ({ ...f, organisation_id: e.target.value }))}>
                  <option value="">Choose…</option>
                  {(info?.organisations || []).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}{o.package_name ? ` — ${o.package_name}` : ''}{o.price_monthly ? ` (${money(o.price_monthly)}/month)` : ''}{!o.is_active ? ' — inactive' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Billing month</label>
                <input type="month" required value={issue.period}
                  onChange={(e) => setIssue((f) => ({ ...f, period: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Amount per month ({info?.currency || 'NAD'})</label>
                <input type="number" step="0.01" min="0.01"
                  placeholder={issueOrg?.price_monthly ? `${issueOrg.price_monthly} (package price)` : 'Required: no package price'}
                  value={issue.amount}
                  onChange={(e) => setIssue((f) => ({ ...f, amount: e.target.value }))} />
              </div>
              <div className="form-group form-group--full">
                <label>Description (optional)</label>
                <input type="text" maxLength={500}
                  placeholder={`${issueOrg?.package_name ? `${issueOrg.package_name} package` : 'PIP Intel'} subscription — each month billed`}
                  value={issue.description}
                  onChange={(e) => setIssue((f) => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Due date (optional)</label>
                <input type="date" min={todayIso()} value={issue.due_date}
                  onChange={(e) => setIssue((f) => ({ ...f, due_date: e.target.value }))} />
                <p style={{ ...muted, margin: '0.3rem 0 0' }}>Default: the last day of the billing month, or 7 days from today for a past month.</p>
              </div>
              <div className="form-group">
                <label>Notes on the invoice (optional)</label>
                <textarea rows={2} maxLength={2000} value={issue.notes}
                  onChange={(e) => setIssue((f) => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="form-group form-group--full">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                  <input type="checkbox" checked={issue.send} style={{ width: 'auto' }}
                    onChange={(e) => setIssue((f) => ({ ...f, send: e.target.checked }))} />
                  Email it now to the organisation's managers{info?.cc?.length ? ` (cc ${info.cc.join(', ')})` : ''}
                </label>
              </div>
            </div>
            {issueOrg && (
              <p style={muted}>
                The invoice bills every month from {issueOrg.name}'s onboarding month
                {issueOrg.onboarded_on ? ` (${monthLabel(issueOrg.onboarded_on)}, onboarded ${formatDate(issueOrg.onboarded_on)})` : ''} up
                to {monthLabel(issue.period)} that isn't on an invoice yet — one line per month.
                {issue.period === info?.current_period && (issueOrg.unbilled_months > 0
                  ? ` That's ${plural(issueOrg.unbilled_months, 'month')} right now.`
                  : issueOrg.invoiced_this_month ? ` It is already invoiced up to ${monthLabel(issue.period)}.` : '')}
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-cancel" disabled={busy} onClick={() => setIssue(null)}>Cancel</button>
              <button type="submit" className="export-button" disabled={busy}>{busy ? 'Issuing…' : 'Issue invoice'}</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Undo payment ── */}
      {undoing && (
        <div className="modal-overlay" onClick={() => !busy && setUndoing(null)}>
          <form className="modal-content" onClick={(e) => e.stopPropagation()} onSubmit={submitUndo} style={{ maxWidth: 520 }}>
            <button type="button" className="modal-close" onClick={() => !busy && setUndoing(null)}>×</button>
            <h2 style={{ marginTop: 0 }}><FaUndo style={{ marginRight: '0.4rem' }} />Undo payment</h2>
            <p style={{ marginTop: 0 }}>
              {money(undoing.payment.amount, undoing.invoice.currency)} paid {formatDate(undoing.payment.paid_on)} on {undoing.invoice.invoice_number}.
              It stays on record, marked undone, and no longer counts towards the invoice. Your name and reason go in the audit trail;
              the organisation managers are emailed the updated invoice (without your reason).
            </p>
            <div className="form-group">
              <label>Reason</label>
              <textarea rows={3} required minLength={3} maxLength={500} value={undoing.reason} autoFocus
                onChange={(e) => setUndoing((u) => ({ ...u, reason: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-cancel" disabled={busy} onClick={() => setUndoing(null)}>Cancel</button>
              <button type="submit" className="export-button" disabled={busy}>{busy ? 'Undoing…' : 'Undo payment'}</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Void ── */}
      {voiding && (
        <div className="modal-overlay" onClick={() => !busy && setVoiding(null)}>
          <form className="modal-content" onClick={(e) => e.stopPropagation()} onSubmit={submitVoid} style={{ maxWidth: 520 }}>
            <button type="button" className="modal-close" onClick={() => !busy && setVoiding(null)}>×</button>
            <h2 style={{ marginTop: 0 }}><FaBan style={{ marginRight: '0.4rem' }} />Void invoice {voiding.invoice.invoice_number}</h2>
            <p style={{ marginTop: 0 }}>
              {voiding.invoice.organisation_name} · {periodLabel(voiding.invoice.period_start, voiding.invoice.period_end)} · {money(voiding.invoice.total, voiding.invoice.currency)}.
              The billing run won't invoice these months again; issue a new invoice by hand if needed.
            </p>
            <div className="form-group">
              <label>Reason</label>
              <textarea rows={3} required minLength={3} maxLength={500} value={voiding.reason}
                onChange={(e) => setVoiding((v) => ({ ...v, reason: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-cancel" disabled={busy} onClick={() => setVoiding(null)}>Cancel</button>
              <button type="submit" className="export-button" disabled={busy}>{busy ? 'Voiding…' : 'Void invoice'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default ManageInvoices;
